import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  UserCheck,
  ListChecks,
  MessageSquareWarning,
  BookOpen,
  LifeBuoy,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Badge,
  Button,
  Card,
} from '@/components/eds';

/**
 * Phase 2 Validation Centre — lightweight, user-facing validation support for
 * the GIISDEC Pilot Production environment.
 *
 * Deliberately NOT a duplicate of the Admin Web governance dashboard: it only
 * tells the signed-in user what Phase 2 covers, what they should review, and
 * how to report findings — reusing existing routes/tabs (support, help
 * launcher) rather than introducing new workflows or endpoints.
 */

interface Props {
  audience: 'investor' | 'gis';
  /** Existing dashboard tab-jump callback (e.g. jump to the support tab). */
  onJump?: (tab: string) => void;
}

const SCOPE: Record<Props['audience'], string[]> = {
  investor: [
    'Investor sign in',
    'Forgot Password & Password Reset',
    'Investor profile & KYC',
    'Investor workflow (LOI → NDA → Payment Validation → MOU)',
    'Agreement Centre',
    'Invoice & payment visibility',
    'Document Centre',
    'Notifications',
    'Information requests',
  ],
  gis: [
    'GIS user sign in',
    'Forgot Password & Password Reset',
    'GIS workspace landing',
    'Permitted layer catalog & map viewer',
    'Dataset access & classifications',
    'Spatial query & export requests',
    'Information requests',
    'Notifications',
  ],
};

const SCENARIOS: Record<Props['audience'], string[]> = {
  investor: [
    'Review your dashboard and continue with the next action shown for your investor journey.',
    'Open the Agreement Centre to review your agreements and signature status.',
    'Review your invoices and payment status from the Payments tab.',
    'Use the Document Centre to access and manage your available documents.',
    'Use Forgot Password if you need to securely reset access to your account.',
    'Open the Help Centre whenever you need guidance using your investor workspace.',
  ],
  gis: [
    'Open your GIS workspace to access the layers permitted for your account.',
    'Use the map workspace to work with permitted layers and review restricted access.',
    'Review available datasets, classifications, and publication status.',
    'Submit spatial queries and export requests for layers available to your account.',
    'Use Forgot Password if you need to securely reset access to your account.',
    'Open the Help Centre whenever you need guidance on GIS access and services.',
  ],
};

const Phase2ValidationCentre: React.FC<Props> = ({ audience, onJump }) => {
  const { profile, user } = useAuth();

  const roleLabel =
    profile?.role ||
    (audience === 'investor' ? 'Investor' : 'GIS / Geology User');

  return (
    <Card
      aria-labelledby="p2vc-heading"
      className="p-5"
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <ClipboardCheck
          className="h-5 w-5 text-eds-emerald-2"
          aria-hidden="true"
        />

        <h3
          id="p2vc-heading"
          className="font-eds-display font-semibold text-eds-fg"
        >
          {audience === 'investor' ? 'Investor Portal Guide' : 'GIS Workspace Guide'}
        </h3>

        {audience === 'gis' && (
          <Badge tone="success" className="ml-auto">
            GIISDEC National Project — Phase I
          </Badge>
        )}
      </div>

      <p className="mb-4 font-eds-body text-sm text-eds-muted-fg">
        Use this guide to navigate the services available to you through the
        GIISDEC Geological Information &amp; Investment Platform (GIISDEC GIIP) and manage your
        activities from your authenticated workspace.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="p-4" elevation="none">
          <div className="mb-2 flex items-center gap-2">
            <UserCheck
              className="h-4 w-4 text-eds-fg"
              aria-hidden="true"
            />
            <h4 className="font-eds-display text-sm font-semibold text-eds-fg">
              Your access
            </h4>
          </div>

          <dl className="space-y-1 font-eds-body text-sm">
            <div className="flex gap-2">
              <dt className="w-28 shrink-0 text-eds-muted-fg">
                Participant
              </dt>
              <dd className="break-all text-eds-fg">
                {profile?.full_name || user?.email || '—'}
              </dd>
            </div>

            <div className="flex gap-2">
              <dt className="w-28 shrink-0 text-eds-muted-fg">
                Assigned role
              </dt>
              <dd className="capitalize text-eds-fg">
                {String(roleLabel).replace(/[_-]/g, ' ')}
              </dd>
            </div>

            <div className="flex gap-2">
              <dt className="w-28 shrink-0 text-eds-muted-fg">
                Status
              </dt>
              <dd>
                {/* P9E: signing in is NOT an entitlement. This states the
                    session only; GIS and restricted-data access are decided
                    by the server per resource (Investor GIS: P9E entitlement). */}
                <Badge tone="neutral">Signed in</Badge>
              </dd>
            </div>
          </dl>

          <p className="mt-2 font-eds-body text-xs text-eds-muted-fg">
            Signing in does not by itself grant access to GIS layers or restricted
            data. Access is determined by GIISDEC authorization controls for each
            service.
          </p>
        </Card>

        <Card className="p-4" elevation="none">
          <div className="mb-2 flex items-center gap-2">
            <ListChecks
              className="h-4 w-4 text-eds-fg"
              aria-hidden="true"
            />
            <h4 className="font-eds-display text-sm font-semibold text-eds-fg">
              Available services
            </h4>
          </div>

          <ul className="list-disc space-y-1 pl-5 font-eds-body text-sm text-eds-fg">
            {SCOPE[audience].map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </Card>

        <Card className="p-4 md:col-span-2" elevation="none">
          <div className="mb-2 flex items-center gap-2">
            <BookOpen
              className="h-4 w-4 text-eds-fg"
              aria-hidden="true"
            />
            <h4 className="font-eds-display text-sm font-semibold text-eds-fg">
              Explore your workspace
            </h4>
          </div>

          <ol className="list-decimal space-y-1 pl-5 font-eds-body text-sm text-eds-fg">
            {SCENARIOS[audience].map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </Card>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {onJump ? (
          <Button
            type="button"
            onClick={() => onJump('support')}
            leftIcon={
              <MessageSquareWarning
                className="h-4 w-4"
                aria-hidden="true"
              />
            }
          >
            Get support
          </Button>
        ) : (
          <Link
            to="/contact"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-eds-md bg-eds-primary px-5 font-eds-body text-sm font-semibold text-eds-primary-fg shadow-eds-sm transition-all hover:bg-eds-emerald-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2"
          >
            <MessageSquareWarning
              className="h-4 w-4"
              aria-hidden="true"
            />
            Contact GIISDEC
          </Link>
        )}

        <Link
          to="/contact"
          className="inline-flex h-11 items-center justify-center gap-2 rounded-eds-md border border-eds-border bg-transparent px-5 font-eds-body text-sm font-semibold text-eds-fg transition-all hover:bg-eds-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2"
        >
          <LifeBuoy
            className="h-4 w-4"
            aria-hidden="true"
          />
          Contact GIISDEC
        </Link>

        <p className="basis-full font-eds-body text-xs text-eds-muted-fg sm:basis-auto">
          Your workspace guide is available from the Help Centre launcher at any time.
        </p>
      </div>
    </Card>
  );
};

export default Phase2ValidationCentre;
